import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
  Cloud, 
  Thermometer, 
  Wind, 
  Droplets,
  AlertTriangle,
  CheckCircle,
  Shield,
  Clock,
  RefreshCw,
  MapPin,
  Snowflake,
  AlertCircle,
  Settings,
  Car,
  ChevronDown,
  ChevronUp,
  Route,
  Eye,
  Plus,
  Pencil,
  Trash2,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RISK_COLORS = {
  normal: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', icon: CheckCircle },
  caution: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400', icon: AlertCircle },
  warning: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', icon: AlertTriangle },
  critical: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', icon: AlertTriangle }
};

export default function WeatherPage() {
  const { user } = useAuth();
  const [weatherData, setWeatherData] = useState([]);
  const [thresholds, setThresholds] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [overrideForm, setOverrideForm] = useState({ reason: '', duration_hours: '4' });
  const [submittingOverride, setSubmittingOverride] = useState(false);
  const [thresholdsDialogOpen, setThresholdsDialogOpen] = useState(false);
  const [editThresholds, setEditThresholds] = useState({});
  const [recentOverrides, setRecentOverrides] = useState([]);
  const [roadConditions, setRoadConditions] = useState([]);
  const [bcRoadConditions, setBcRoadConditions] = useState(null);
  const [loadingRoads, setLoadingRoads] = useState(true);
  const [expandedHighway, setExpandedHighway] = useState(null);
  const [roadRegion, setRoadRegion] = useState('bc'); // 'bc' or 'alberta' - BC is default (98% of operations)
  const [activeTab, setActiveTab] = useState('weather'); // 'weather', 'roads', or 'traffic'
  
  // Traffic state
  const [trafficOrigin, setTrafficOrigin] = useState('');
  const [trafficDestination, setTrafficDestination] = useState('');
  const [trafficResult, setTrafficResult] = useState(null);
  const [loadingTraffic, setLoadingTraffic] = useState(false);
  
  // Quick routes state
  const [quickRoutes, setQuickRoutes] = useState([]);
  const [quickRouteDialogOpen, setQuickRouteDialogOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [quickRouteForm, setQuickRouteForm] = useState({ origin: '', destination: '', name: '' });
  const [savingRoute, setSavingRoute] = useState(false);
  
  // Location management state
  const [locations, setLocations] = useState([]);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationForm, setLocationForm] = useState({ name: '', lat: '', lon: '', province: 'BC' });
  const [savingLocation, setSavingLocation] = useState(false);
  
  const isManager = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    fetchWeatherData();
    fetchOverrides();
    fetchLocations();
    // Only fetch BC road conditions on page load - Alberta fetched on demand
    fetchBcRoadConditions();
    fetchQuickRoutes();
    
    // Refresh weather every 5 minutes
    const interval = setInterval(fetchWeatherData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/weather/locations`);
      setLocations(response.data.locations || []);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const handleAddLocation = () => {
    setEditingLocation(null);
    setLocationForm({ name: '', lat: '', lon: '', province: 'BC' });
    setLocationDialogOpen(true);
  };

  const handleEditLocation = (location) => {
    setEditingLocation(location);
    setLocationForm({
      name: location.name,
      lat: String(location.lat),
      lon: String(location.lon),
      province: location.province || 'BC'
    });
    setLocationDialogOpen(true);
  };

  const handleSaveLocation = async () => {
    if (!locationForm.name) {
      toast.error('Please enter a location name');
      return;
    }

    setSavingLocation(true);
    try {
      if (editingLocation) {
        await axios.put(`${API}/weather/locations/${editingLocation.id}`, locationForm);
        toast.success('Location updated');
      } else {
        await axios.post(`${API}/weather/locations`, locationForm);
        toast.success('Location added');
      }
      setLocationDialogOpen(false);
      fetchLocations();
      fetchWeatherData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save location');
    } finally {
      setSavingLocation(false);
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;
    
    try {
      await axios.delete(`${API}/weather/locations/${locationId}`);
      toast.success('Location deleted');
      fetchLocations();
      fetchWeatherData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete location');
    }
  };

  const fetchWeatherData = async () => {
    try {
      const response = await axios.get(`${API}/weather`);
      setWeatherData(response.data.weather);
      setThresholds(response.data.thresholds);
      setEditThresholds(response.data.thresholds);
    } catch (error) {
      toast.error('Failed to fetch weather data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchOverrides = async () => {
    try {
      const response = await axios.get(`${API}/weather/overrides`);
      setRecentOverrides(response.data.overrides);
    } catch (error) {
      // Ignore - user might not have permission
    }
  };

  const fetchRoadConditions = async () => {
    try {
      setLoadingRoads(true);
      const response = await axios.get(`${API}/roads/conditions`);
      setRoadConditions(response.data.highways);
    } catch (error) {
      console.error('Failed to fetch road conditions:', error);
    } finally {
      setLoadingRoads(false);
    }
  };

  const fetchBcRoadConditions = async () => {
    try {
      const response = await axios.get(`${API}/roads/bc/conditions`);
      setBcRoadConditions(response.data);
    } catch (error) {
      console.error('Failed to fetch BC road conditions:', error);
    }
  };

  const checkTraffic = async () => {
    if (!trafficOrigin || !trafficDestination) {
      toast.error('Please enter both origin and destination');
      return;
    }
    
    try {
      setLoadingTraffic(true);
      setTrafficResult(null);
      const response = await axios.get(`${API}/traffic/check`, {
        params: { origin: trafficOrigin, destination: trafficDestination }
      });
      setTrafficResult(response.data);
      if (response.data.success) {
        toast.success('Traffic data retrieved');
      }
    } catch (error) {
      console.error('Failed to fetch traffic:', error);
      toast.error('Failed to get traffic data. Try different locations.');
      setTrafficResult({ success: false, error: error.response?.data?.detail || 'Failed to fetch traffic data' });
    } finally {
      setLoadingTraffic(false);
    }
  };

  const fetchQuickRoutes = async () => {
    try {
      const response = await axios.get(`${API}/traffic/quick-routes`);
      setQuickRoutes(response.data.routes || []);
    } catch (error) {
      console.error('Failed to fetch quick routes:', error);
    }
  };

  const openAddRouteDialog = () => {
    setEditingRoute(null);
    setQuickRouteForm({ origin: '', destination: '', name: '' });
    setQuickRouteDialogOpen(true);
  };

  const openEditRouteDialog = (route) => {
    setEditingRoute(route);
    setQuickRouteForm({ 
      origin: route.origin, 
      destination: route.destination, 
      name: route.name || '' 
    });
    setQuickRouteDialogOpen(true);
  };

  const saveQuickRoute = async () => {
    if (!quickRouteForm.origin || !quickRouteForm.destination) {
      toast.error('Please enter both origin and destination');
      return;
    }

    try {
      setSavingRoute(true);
      if (editingRoute) {
        await axios.put(`${API}/traffic/quick-routes/${editingRoute.id}`, quickRouteForm);
        toast.success('Route updated');
      } else {
        await axios.post(`${API}/traffic/quick-routes`, quickRouteForm);
        toast.success('Route added');
      }
      setQuickRouteDialogOpen(false);
      fetchQuickRoutes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save route');
    } finally {
      setSavingRoute(false);
    }
  };

  const deleteQuickRoute = async (routeId) => {
    if (!window.confirm('Delete this quick route?')) return;
    
    try {
      await axios.delete(`${API}/traffic/quick-routes/${routeId}`);
      toast.success('Route deleted');
      fetchQuickRoutes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete route');
    }
  };

  const getTrafficColor = (severity) => {
    switch(severity) {
      case 'light': return { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400' };
      case 'moderate': return { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400' };
      case 'heavy': return { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400' };
      case 'severe': return { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400' };
      default: return { bg: 'bg-gray-500/10', border: 'border-gray-500/30', text: 'text-gray-400' };
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchWeatherData();
    fetchRoadConditions();
  };

  const getRoadConditionColor = (severity) => {
    switch(severity) {
      case 'good': return { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400' };
      case 'moderate': return { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400' };
      case 'caution': return { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400' };
      case 'warning': return { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400' };
      case 'critical': return { bg: 'bg-red-500/20', border: 'border-red-500/50', text: 'text-red-400' };
      default: return { bg: 'bg-gray-500/10', border: 'border-gray-500/30', text: 'text-gray-400' };
    }
  };

  const formatUnixTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000).toLocaleString('en-CA', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleOverride = async () => {
    if (!selectedLocation || !overrideForm.reason) {
      toast.error('Please provide a reason for the override');
      return;
    }

    setSubmittingOverride(true);
    try {
      await axios.post(`${API}/weather/override`, {
        location_id: selectedLocation.location.id,
        reason: overrideForm.reason,
        duration_hours: parseInt(overrideForm.duration_hours)
      });
      
      toast.success(`Override approved for ${selectedLocation.location.name}`);
      setOverrideDialogOpen(false);
      setOverrideForm({ reason: '', duration_hours: '4' });
      fetchWeatherData();
      fetchOverrides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create override');
    } finally {
      setSubmittingOverride(false);
    }
  };

  const handleUpdateThresholds = async () => {
    try {
      await axios.put(`${API}/weather/thresholds`, editThresholds);
      toast.success('Temperature thresholds updated');
      setThresholdsDialogOpen(false);
      fetchWeatherData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update thresholds');
    }
  };

  const formatTime = (isoString) => {
    return new Date(isoString).toLocaleTimeString('en-CA', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-foreground">Weather & Operations</h1>
          <p className="text-muted-foreground mt-1">
            Real-time weather and road conditions for safe dispatch
          </p>
        </div>
        <div className="flex gap-3">
          {user?.role === 'admin' && (
            <Dialog open={thresholdsDialogOpen} onOpenChange={setThresholdsDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="edit-thresholds-btn">
                  <Settings className="w-4 h-4 mr-2" />
                  Thresholds
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Temperature Thresholds</DialogTitle>
                  <DialogDescription>
                    Configure temperature thresholds for risk levels
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 rounded-full bg-emerald-400" />
                        Normal (above)
                      </Label>
                      <Input
                        type="number"
                        value={editThresholds.normal || ''}
                        onChange={(e) => setEditThresholds({ ...editThresholds, normal: parseFloat(e.target.value) })}
                        placeholder="-15"
                      />
                    </div>
                    <div>
                      <Label className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 rounded-full bg-yellow-400" />
                        Caution (above)
                      </Label>
                      <Input
                        type="number"
                        value={editThresholds.caution || ''}
                        onChange={(e) => setEditThresholds({ ...editThresholds, caution: parseFloat(e.target.value) })}
                        placeholder="-25"
                      />
                    </div>
                    <div>
                      <Label className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 rounded-full bg-orange-400" />
                        Warning (above)
                      </Label>
                      <Input
                        type="number"
                        value={editThresholds.warning || ''}
                        onChange={(e) => setEditThresholds({ ...editThresholds, warning: parseFloat(e.target.value) })}
                        placeholder="-35"
                      />
                    </div>
                    <div>
                      <Label className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 rounded-full bg-red-400" />
                        Critical (below)
                      </Label>
                      <Input
                        type="number"
                        value={editThresholds.critical || ''}
                        onChange={(e) => setEditThresholds({ ...editThresholds, critical: parseFloat(e.target.value) })}
                        placeholder="-40"
                        disabled
                      />
                      <p className="text-xs text-muted-foreground mt-1">Uses warning threshold</p>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button variant="outline" onClick={() => setThresholdsDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleUpdateThresholds}>Save Thresholds</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
          <Button onClick={handleRefresh} variant="outline" disabled={refreshing} data-testid="refresh-weather-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Legend */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-emerald-400" />
              <span className="text-sm text-muted-foreground">Normal (above {thresholds.normal}°C)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-yellow-400" />
              <span className="text-sm text-muted-foreground">Caution ({thresholds.caution}°C to {thresholds.normal}°C)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-orange-400" />
              <span className="text-sm text-muted-foreground">Warning ({thresholds.warning}°C to {thresholds.caution}°C)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-red-400" />
              <span className="text-sm text-muted-foreground">Critical (below {thresholds.warning}°C) - Override required</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setActiveTab('weather')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'weather' 
              ? 'border-primary text-primary' 
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Thermometer className="w-4 h-4 inline mr-2" />
          Weather Conditions
        </button>
        <button
          onClick={() => setActiveTab('roads')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'roads' 
              ? 'border-primary text-primary' 
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Car className="w-4 h-4 inline mr-2" />
          Road Conditions
          {(roadConditions || []).some(r => r.severity === 'critical' || r.severity === 'warning') && (
            <span className="ml-2 w-2 h-2 rounded-full bg-red-400 inline-block" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('traffic')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'traffic' 
              ? 'border-primary text-primary' 
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Route className="w-4 h-4 inline mr-2" />
          Traffic Check
        </button>
      </div>

      {/* Weather Tab Content */}
      {activeTab === 'weather' && (
        <>
          {/* Location Management Header */}
          {isManager && (
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                {weatherData.length} monitored locations
              </p>
              <Button onClick={handleAddLocation} size="sm" variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Add Location
              </Button>
            </div>
          )}

          {/* Weather Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {weatherData.map((item) => {
          const riskStyle = RISK_COLORS[item.risk.level] || RISK_COLORS.normal;
          const RiskIcon = riskStyle.icon;
          const locationData = locations.find(l => l.id === item.location.id);
          
          return (
            <Card 
              key={item.location.id} 
              className={`${riskStyle.border} border-2 card-hover relative`}
              data-testid={`weather-card-${item.location.id}`}
            >
              {/* Edit/Delete buttons for managers */}
              {isManager && locationData && (
                <div className="absolute top-2 right-2 flex gap-1">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6 opacity-50 hover:opacity-100"
                    onClick={(e) => { e.stopPropagation(); handleEditLocation(locationData); }}
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6 opacity-50 hover:opacity-100 hover:text-red-500"
                    onClick={(e) => { e.stopPropagation(); handleDeleteLocation(item.location.id); }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              )}
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-primary" />
                      {item.location.name}
                    </CardTitle>
                    <CardDescription>{item.location.province}</CardDescription>
                  </div>
                  <Badge variant="outline" className={`${riskStyle.bg} ${riskStyle.text} ${riskStyle.border}`}>
                    {item.risk.level.toUpperCase()}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Temperature */}
                <div className="text-center py-4">
                  <div className="flex items-center justify-center gap-2">
                    <Thermometer className={`w-8 h-8 ${riskStyle.text}`} />
                    <span className={`text-5xl font-bold ${riskStyle.text}`}>
                      {Math.round(item.weather.temp)}°
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Feels like {Math.round(item.weather.feels_like)}°C
                  </p>
                </div>

                {/* Conditions */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <Cloud className="w-4 h-4" />
                      Conditions
                    </span>
                    <span className="text-foreground">{item.weather.description}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <Wind className="w-4 h-4" />
                      Wind
                    </span>
                    <span className="text-foreground">{Math.round(item.weather.wind_speed)} km/h</span>
                  </div>
                  {item.weather.wind_gust > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <Wind className="w-4 h-4" />
                        Gusts
                      </span>
                      <span className="text-foreground">{Math.round(item.weather.wind_gust)} km/h</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <Droplets className="w-4 h-4" />
                      Humidity
                    </span>
                    <span className="text-foreground">{item.weather.humidity}%</span>
                  </div>
                </div>

                {/* Status Message */}
                <div className={`p-3 rounded-lg ${riskStyle.bg} ${riskStyle.border} border`}>
                  <div className="flex items-start gap-2">
                    <RiskIcon className={`w-5 h-5 ${riskStyle.text} flex-shrink-0 mt-0.5`} />
                    <div>
                      <p className={`text-sm font-medium ${riskStyle.text}`}>
                        {item.risk.message}
                      </p>
                      {item.risk.override_active && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Override by {item.risk.override_by} • Expires {formatTime(item.risk.override_expires)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Override Button */}
                {item.risk.override_required && !item.risk.override_active && isManager && (
                  <Button 
                    className="w-full" 
                    variant="destructive"
                    onClick={() => {
                      setSelectedLocation(item);
                      setOverrideDialogOpen(true);
                    }}
                    data-testid={`override-btn-${item.location.id}`}
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Approve Override
                  </Button>
                )}

                {item.risk.can_depart && (
                  <div className="flex items-center justify-center gap-2 text-emerald-400">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Can Depart</span>
                  </div>
                )}

                {/* Mock Data Indicator */}
                {item.weather.is_mock && (
                  <p className="text-xs text-center text-muted-foreground">
                    ⚠️ Demo data - Add weather API key for live data
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
          </div>

          {/* Recent Overrides - Weather Tab */}
          {isManager && recentOverrides.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Overrides</CardTitle>
                <CardDescription>Weather overrides from the last 7 days</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {recentOverrides.slice(0, 5).map((override) => (
                    <div 
                      key={override.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-secondary/50"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          new Date(override.expires_at) > new Date() && override.status === 'approved'
                            ? 'bg-emerald-400' 
                            : 'bg-muted-foreground'
                        }`} />
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {override.location_name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {override.reason} • By {override.approved_by_name}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className={
                          override.status === 'cancelled' 
                            ? 'bg-red-500/10 text-red-400' 
                            : new Date(override.expires_at) > new Date()
                              ? 'bg-emerald-500/10 text-emerald-400'
                              : 'bg-muted text-muted-foreground'
                        }>
                          {override.status === 'cancelled' 
                            ? 'Cancelled' 
                            : new Date(override.expires_at) > new Date()
                              ? 'Active'
                              : 'Expired'
                          }
                        </Badge>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(override.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Road Conditions Tab Content */}
      {activeTab === 'roads' && (
        <div className="space-y-4">
          {/* Region Selector - BC first (98% of operations) */}
          <div className="flex gap-2">
            <Button
              variant={roadRegion === 'bc' ? 'default' : 'outline'}
              onClick={() => setRoadRegion('bc')}
              className="flex items-center gap-2"
            >
              <MapPin className="w-4 h-4" />
              British Columbia (DriveBC)
              {bcRoadConditions?.summary?.critical > 0 && (
                <span className="w-2 h-2 rounded-full bg-red-400 inline-block ml-1" />
              )}
            </Button>
            <Button
              variant={roadRegion === 'alberta' ? 'default' : 'outline'}
              onClick={() => {
                setRoadRegion('alberta');
                // Only fetch Alberta data when tab is clicked (on-demand)
                if (!roadConditions || roadConditions.length === 0) {
                  fetchRoadConditions();
                }
              }}
              className="flex items-center gap-2"
            >
              <MapPin className="w-4 h-4" />
              Alberta (511)
              {(roadConditions || []).some(r => r.severity === 'critical' || r.severity === 'warning') && (
                <span className="w-2 h-2 rounded-full bg-red-400 inline-block ml-1" />
              )}
            </Button>
          </div>

          {/* Alberta Roads */}
          {roadRegion === 'alberta' && (
            <>
              {/* Road Conditions Legend */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-wrap gap-6">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-emerald-400" />
                      <span className="text-sm text-muted-foreground">Bare & Dry</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-yellow-400" />
                      <span className="text-sm text-muted-foreground">Partly Covered Snow</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-orange-400" />
                      <span className="text-sm text-muted-foreground">Covered Snow/Ice</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-red-400" />
                      <span className="text-sm text-muted-foreground">Closed / Travel Not Recommended</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Highway List */}
              {loadingRoads ? (
                <div className="flex items-center justify-center min-h-[200px]">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="space-y-3">
                  {(roadConditions || []).map((highway) => {
                    const condStyle = getRoadConditionColor(highway.severity);
                    const isExpanded = expandedHighway === highway.id;
                    
                    return (
                  <Card 
                    key={highway.id} 
                    className={`${condStyle.border} border overflow-hidden`}
                  >
                    <div 
                      className="p-4 cursor-pointer hover:bg-secondary/30 transition-colors"
                      onClick={() => setExpandedHighway(isExpanded ? null : highway.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${condStyle.bg}`}>
                            <Route className={`w-5 h-5 ${condStyle.text}`} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-foreground flex items-center gap-2">
                              {highway.name}
                              <Badge variant="outline" className={`${condStyle.bg} ${condStyle.text} ${condStyle.border}`}>
                                {highway.condition_label}
                              </Badge>
                            </h3>
                            <p className="text-sm text-muted-foreground">{highway.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right hidden sm:block">
                            <p className="text-sm text-muted-foreground">
                              {highway.segment_count} segments monitored
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Updated: {formatUnixTime(highway.last_updated)}
                            </p>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                      </div>

                      {/* Conditions Breakdown - Always visible */}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {Object.entries(highway.conditions_breakdown || {}).map(([condition, count]) => {
                          const cStyle = getRoadConditionColor(
                            condition === 'Bare Dry' || condition === 'Bare Wet' ? 'good' :
                            condition === 'Ptly Cvd Snw' ? 'moderate' :
                            condition === 'Closed' || condition === 'Travel Not Recommended' ? 'critical' : 'caution'
                          );
                          return (
                            <span 
                              key={condition}
                              className={`text-xs px-2 py-1 rounded ${cStyle.bg} ${cStyle.text}`}
                            >
                              {condition}: {count}
                            </span>
                          );
                        })}
                      </div>
                    </div>

                    {/* Expanded Segments */}
                    {isExpanded && highway.segments && (
                      <div className="border-t border-border bg-secondary/20 p-4">
                        <h4 className="text-sm font-medium mb-3 text-muted-foreground">Road Segments</h4>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {highway.segments.map((segment, idx) => {
                            const segStyle = getRoadConditionColor(
                              segment.condition === 'Bare Dry' || segment.condition === 'Bare Wet' ? 'good' :
                              segment.condition === 'Ptly Cvd Snw' ? 'moderate' :
                              segment.condition === 'Closed' || segment.condition === 'Travel Not Recommended' ? 'critical' : 'caution'
                            );
                            return (
                              <div 
                                key={idx}
                                className={`p-2 rounded ${segStyle.bg} flex items-center justify-between`}
                              >
                                <div>
                                  <p className="text-sm font-medium text-foreground">{segment.location || 'Unknown'}</p>
                                  <p className="text-xs text-muted-foreground">{segment.area}</p>
                                </div>
                                <div className="text-right">
                                  <Badge variant="outline" className={`${segStyle.text} ${segStyle.border}`}>
                                    {segment.condition}
                                  </Badge>
                                  {segment.visibility && segment.visibility !== 'N/A' && (
                                    <p className="text-xs text-muted-foreground mt-1 flex items-center justify-end gap-1">
                                      <Eye className="w-3 h-3" />
                                      {segment.visibility}
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}

          {/* Data Source Attribution */}
          <p className="text-xs text-center text-muted-foreground">
            Road condition data provided by 511 Alberta • Refreshed with weather data
          </p>
        </>
          )}

          {/* BC Roads */}
          {roadRegion === 'bc' && (
            <>
              {/* BC Road Conditions Legend */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-wrap gap-6">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-emerald-400" />
                      <span className="text-sm text-muted-foreground">Normal</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-orange-400" />
                      <span className="text-sm text-muted-foreground">Warning / Moderate</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-red-400" />
                      <span className="text-sm text-muted-foreground">Critical / Major</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* BC Summary */}
              {bcRoadConditions?.summary && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex flex-wrap gap-4 text-sm">
                      <span className="text-muted-foreground">
                        <strong>{bcRoadConditions.summary.total_events}</strong> active events
                      </span>
                      {bcRoadConditions.summary.critical > 0 && (
                        <span className="text-red-400">
                          <strong>{bcRoadConditions.summary.critical}</strong> critical
                        </span>
                      )}
                      {bcRoadConditions.summary.warning > 0 && (
                        <span className="text-orange-400">
                          <strong>{bcRoadConditions.summary.warning}</strong> warnings
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* BC Highway Events */}
              {!bcRoadConditions ? (
                <div className="flex items-center justify-center min-h-[200px]">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : bcRoadConditions.highways?.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
                    <p className="text-foreground font-medium">All Clear in BC</p>
                    <p className="text-sm text-muted-foreground">No active road events reported</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {bcRoadConditions.highways.map((highway) => {
                    const condStyle = getRoadConditionColor(highway.severity);
                    const isExpanded = expandedHighway === highway.highway_id;
                    
                    return (
                      <Card 
                        key={highway.highway_id} 
                        className={`${condStyle.border} border overflow-hidden`}
                      >
                        <div 
                          className="p-4 cursor-pointer hover:bg-secondary/30 transition-colors"
                          onClick={() => setExpandedHighway(isExpanded ? null : highway.highway_id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={`p-2 rounded-lg ${condStyle.bg}`}>
                                <Route className={`w-5 h-5 ${condStyle.text}`} />
                              </div>
                              <div>
                                <h3 className="font-semibold text-foreground flex items-center gap-2">
                                  {highway.highway}
                                  <Badge variant="outline" className={`${condStyle.bg} ${condStyle.text} ${condStyle.border}`}>
                                    {highway.severity === 'critical' ? 'MAJOR' : highway.severity === 'warning' ? 'MODERATE' : 'MINOR'}
                                  </Badge>
                                </h3>
                                <p className="text-sm text-muted-foreground">{highway.condition}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right hidden sm:block">
                                <p className="text-sm text-muted-foreground">
                                  {highway.event_count} event(s)
                                </p>
                              </div>
                              {isExpanded ? (
                                <ChevronUp className="w-5 h-5 text-muted-foreground" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-muted-foreground" />
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Expanded Events */}
                        {isExpanded && highway.events && (
                          <div className="border-t border-border bg-secondary/20 p-4">
                            <div className="space-y-3">
                              {highway.events.map((event, idx) => (
                                <div key={event.id || idx} className="p-3 bg-background/50 rounded-lg">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                      <p className="font-medium text-sm">{event.headline}</p>
                                      <p className="text-xs text-muted-foreground mt-1">{event.description?.substring(0, 200)}...</p>
                                      {event.area && (
                                        <p className="text-xs text-muted-foreground mt-1">
                                          <MapPin className="w-3 h-3 inline mr-1" />
                                          {event.area}
                                        </p>
                                      )}
                                    </div>
                                    <Badge variant="outline" className={`text-xs ${
                                      event.event_type === 'CONSTRUCTION' ? 'bg-yellow-500/20 text-yellow-400' :
                                      event.event_type === 'INCIDENT' ? 'bg-red-500/20 text-red-400' :
                                      event.event_type === 'WEATHER_CONDITION' ? 'bg-blue-500/20 text-blue-400' :
                                      'bg-gray-500/20 text-gray-400'
                                    }`}>
                                      {event.event_type?.replace(/_/g, ' ')}
                                    </Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Data Source Attribution */}
              <p className="text-xs text-center text-muted-foreground">
                Road condition data provided by DriveBC Open511 API • Real-time updates
              </p>
            </>
          )}
        </div>
      )}

      {/* Traffic Tab Content */}
      {activeTab === 'traffic' && (
        <div className="space-y-4">
          {/* Traffic Checker Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Route className="w-5 h-5 text-primary" />
                Route Traffic Checker
              </CardTitle>
              <CardDescription>
                Check real-time travel time and traffic conditions between any two locations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Origin</Label>
                  <Input
                    placeholder="e.g., Fort St. John, BC"
                    value={trafficOrigin}
                    onChange={(e) => setTrafficOrigin(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && checkTraffic()}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Destination</Label>
                  <Input
                    placeholder="e.g., Fort Nelson, BC"
                    value={trafficDestination}
                    onChange={(e) => setTrafficDestination(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && checkTraffic()}
                  />
                </div>
              </div>
              <Button 
                onClick={checkTraffic} 
                disabled={loadingTraffic || !trafficOrigin || !trafficDestination}
                className="w-full sm:w-auto"
              >
                {loadingTraffic ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Checking Traffic...
                  </>
                ) : (
                  <>
                    <Route className="w-4 h-4 mr-2" />
                    Check Traffic
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Traffic Result */}
          {trafficResult && (
            <Card className={trafficResult.success ? (trafficResult.bc_has_critical ? 'border-red-500' : 'border-border') : 'border-red-500/30'}>
              <CardContent className="p-6">
                {trafficResult.success ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-foreground">
                          {trafficResult.origin} → {trafficResult.destination}
                        </h3>
                        <p className="text-sm text-muted-foreground">{trafficResult.summary}</p>
                        {/* Show primary data source */}
                        {trafficResult.primary_source && (
                          <p className="text-xs text-green-400 mt-1">
                            ✅ Primary source: {trafficResult.primary_source}
                          </p>
                        )}
                        {/* Show if names were normalized */}
                        {(trafficResult.origin_normalized || trafficResult.destination_normalized) && (
                          <p className="text-xs text-blue-400 mt-1">
                            📍 Locations recognized: {trafficResult.origin_input} → {trafficResult.origin}, {trafficResult.destination_input} → {trafficResult.destination}
                          </p>
                        )}
                      </div>
                      <div className="p-3 rounded-lg bg-secondary/50">
                        <span className="text-2xl">{trafficResult.traffic_emoji || '🛣️'}</span>
                      </div>
                    </div>
                    
                    {/* OVERRIDE REQUIRED WARNING */}
                    {trafficResult.requires_override && (
                      <div className="p-4 rounded-lg bg-red-500/20 border-2 border-red-500">
                        <div className="flex items-center gap-3">
                          <AlertTriangle className="w-6 h-6 text-red-400" />
                          <div>
                            <p className="font-bold text-red-400">🚨 MANAGER OVERRIDE REQUIRED</p>
                            <p className="text-sm text-red-300">{trafficResult.override_reason}</p>
                            <p className="text-xs text-muted-foreground mt-1">Driver must get manager approval before departing on this route.</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div className="p-4 rounded-lg bg-secondary/30">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <Clock className="w-4 h-4" />
                          <span className="text-sm">Travel Time</span>
                        </div>
                        <p className="text-xl font-bold text-foreground">{trafficResult.formatted_time || 'N/A'}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-secondary/30">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <MapPin className="w-4 h-4" />
                          <span className="text-sm">Distance</span>
                        </div>
                        <p className="text-xl font-bold text-foreground">{trafficResult.distance_km ? `${trafficResult.distance_km} km` : 'N/A'}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-secondary/30">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <Car className="w-4 h-4" />
                          <span className="text-sm">Traffic (Waze)</span>
                        </div>
                        {trafficResult.waze?.reliability === 'low' || trafficResult.waze?.reliability === 'supplementary' ? (
                          <p className="text-sm text-muted-foreground">
                            {trafficResult.involves_bc ? 'See DriveBC' : 'Limited data'}
                          </p>
                        ) : trafficResult.traffic_severity ? (
                          <p className={`text-xl font-bold ${getTrafficColor(trafficResult.traffic_severity).text}`}>
                            {trafficResult.traffic_severity?.toUpperCase()}
                          </p>
                        ) : (
                          <p className="text-sm text-muted-foreground">N/A</p>
                        )}
                      </div>
                    </div>

                    {/* DriveBC Road Conditions - SHOW PROMINENTLY FOR BC ROUTES */}
                    {trafficResult.involves_bc && trafficResult.drivebc?.success && (
                      <div className={`p-4 rounded-lg ${
                        trafficResult.bc_has_critical ? 'bg-red-500/20 border-2 border-red-500' :
                        trafficResult.bc_has_warning ? 'bg-orange-500/20 border-2 border-orange-500' :
                        'bg-blue-500/10 border border-blue-500/30'
                      }`}>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-foreground flex items-center gap-2">
                            🛣️ Road Conditions (DriveBC)
                          </h4>
                          <Badge variant="outline" className={
                            trafficResult.bc_has_critical ? 'bg-red-500/20 text-red-400' :
                            trafficResult.bc_has_warning ? 'bg-orange-500/20 text-orange-400' :
                            'bg-blue-500/20 text-blue-400'
                          }>
                            {trafficResult.bc_events_count || 0} reports
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">
                          Highways: {trafficResult.bc_highways?.join(', ') || 'N/A'}
                        </p>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {trafficResult.drivebc?.events?.slice(0, 8).map((event, idx) => (
                            <div key={idx} className={`text-sm p-2 rounded ${
                              event.severity === 'critical' ? 'bg-red-500/20' :
                              event.severity === 'warning' ? 'bg-orange-500/20' : 'bg-secondary/50'
                            }`}>
                              <span className={`font-medium ${
                                event.severity === 'critical' ? 'text-red-400' :
                                event.severity === 'warning' ? 'text-orange-400' : 
                                event.event_type === 'WEATHER_CONDITION' ? 'text-blue-400' :
                                event.event_type === 'ROAD_CONDITION' ? 'text-yellow-400' :
                                'text-foreground'
                              }`}>
                                [{event.event_type}]
                              </span>
                              {' '}<span className="text-muted-foreground">{event.highway}:</span>{' '}
                              <span className="text-foreground">{event.description?.substring(0, 100)}{event.description?.length > 100 ? '...' : ''}</span>
                            </div>
                          ))}
                        </div>
                        {(trafficResult.bc_events_count || 0) > 8 && (
                          <p className="text-xs text-muted-foreground mt-2">
                            + {trafficResult.bc_events_count - 8} more reports
                          </p>
                        )}
                      </div>
                    )}

                    {/* 511 Alberta Road Conditions */}
                    {trafficResult.involves_ab && trafficResult.alberta_511?.success && (
                      <div className={`p-4 rounded-lg ${
                        trafficResult.alberta_511.severity === 'critical' ? 'bg-red-500/20 border-2 border-red-500' :
                        trafficResult.alberta_511.severity === 'warning' ? 'bg-orange-500/20 border-2 border-orange-500' :
                        'bg-emerald-500/10 border border-emerald-500/30'
                      }`}>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-foreground flex items-center gap-2">
                            🛣️ Road Conditions (511 Alberta)
                          </h4>
                          <Badge variant="outline" className={
                            trafficResult.alberta_511.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                            trafficResult.alberta_511.severity === 'warning' ? 'bg-orange-500/20 text-orange-400' :
                            'bg-emerald-500/20 text-emerald-400'
                          }>
                            {trafficResult.alberta_511.worst_condition}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">
                          Checked {trafficResult.alberta_511.total_road_segments} road segments • {trafficResult.alberta_511.relevant_conditions?.length || 0} relevant to route
                        </p>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {trafficResult.alberta_511.relevant_conditions?.slice(0, 8).map((condition, idx) => (
                            <div key={idx} className={`text-sm p-2 rounded ${
                              condition.condition === 'Closed' || condition.condition === 'Travel Not Recommended' ? 'bg-red-500/20' :
                              condition.condition === 'Cvd Ice' || condition.condition === 'Cvd Snw' ? 'bg-orange-500/20' :
                              condition.condition === 'Ptly Cvd Snw' || condition.condition === 'Ptly Cvd Ice' ? 'bg-yellow-500/20' :
                              'bg-secondary/50'
                            }`}>
                              <span className={`font-medium ${
                                condition.condition === 'Closed' || condition.condition === 'Travel Not Recommended' ? 'text-red-400' :
                                condition.condition === 'Cvd Ice' || condition.condition === 'Cvd Snw' ? 'text-orange-400' :
                                condition.condition === 'Ptly Cvd Snw' || condition.condition === 'Ptly Cvd Ice' ? 'text-yellow-400' :
                                'text-emerald-400'
                              }`}>
                                [{condition.condition}]
                              </span>
                              {' '}<span className="text-muted-foreground">{condition.highway}:</span>{' '}
                              <span className="text-foreground">{condition.location?.substring(0, 80)}{condition.location?.length > 80 ? '...' : ''}</span>
                            </div>
                          ))}
                        </div>
                        {(trafficResult.alberta_511.relevant_conditions?.length || 0) > 8 && (
                          <p className="text-xs text-muted-foreground mt-2">
                            + {trafficResult.alberta_511.relevant_conditions.length - 8} more road segments
                          </p>
                        )}
                      </div>
                    )}

                    {/* 511 Alberta Error State */}
                    {trafficResult.involves_ab && trafficResult.alberta_511 && !trafficResult.alberta_511.success && (
                      <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                        <h4 className="font-semibold text-yellow-400 flex items-center gap-2 mb-2">
                          ⚠️ 511 Alberta Data Unavailable
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {trafficResult.alberta_511.error || 'Alberta road conditions temporarily unavailable'}
                        </p>
                      </div>
                    )}

                    {trafficResult.traffic_delay_minutes > 5 && trafficResult.traffic_severity && trafficResult.traffic_severity !== 'unknown' && (
                      <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/30">
                        <p className="text-sm text-orange-400 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          Traffic is adding approximately <strong>+{trafficResult.formatted_delay}</strong> to your trip
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <AlertCircle className="w-10 h-10 mx-auto mb-2 text-red-400" />
                    <p className="text-red-400">{trafficResult.error || 'Could not get traffic data'}</p>
                    <p className="text-sm text-muted-foreground mt-1">Try different location names or check spelling</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Quick Routes */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Quick Routes</CardTitle>
                <CardDescription>Common routes - click to check traffic</CardDescription>
              </div>
              {(user?.role === 'admin' || user?.role === 'manager') && (
                <Button size="sm" variant="outline" onClick={openAddRouteDialog}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Route
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {/* Default routes if no custom routes exist */}
                {quickRoutes.length === 0 && [
                  { from: 'Fort St. John, BC', to: 'Fort Nelson, BC' },
                  { from: 'Fort St. John, BC', to: 'Prince George, BC' },
                  { from: 'Fort St. John, BC', to: 'Dawson Creek, BC' },
                  { from: 'Dawson Creek, BC', to: "Hudson's Hope, BC" },
                  { from: 'Prince George, BC', to: 'Kamloops, BC' },
                ].map((route, idx) => (
                  <Button
                    key={`default-${idx}`}
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setTrafficOrigin(route.from);
                      setTrafficDestination(route.to);
                    }}
                    className="text-xs"
                  >
                    {route.from.split(',')[0]} → {route.to.split(',')[0]}
                  </Button>
                ))}
                
                {/* Custom routes from database */}
                {quickRoutes.map((route) => (
                  <div key={route.id} className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setTrafficOrigin(route.origin);
                        setTrafficDestination(route.destination);
                      }}
                      className="text-xs"
                    >
                      {route.name || `${route.origin.split(',')[0]} → ${route.destination.split(',')[0]}`}
                    </Button>
                    {(user?.role === 'admin' || user?.role === 'manager') && (
                      <div className="flex gap-0.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => openEditRouteDialog(route)}
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-red-400 hover:text-red-500"
                          onClick={() => deleteQuickRoute(route.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Data Source Attribution */}
          <p className="text-xs text-center text-muted-foreground">
            Traffic data powered by Waze • Real-time conditions
          </p>
        </div>
      )}

      {/* Quick Route Add/Edit Dialog */}
      <Dialog open={quickRouteDialogOpen} onOpenChange={setQuickRouteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Route className="w-5 h-5 text-primary" />
              {editingRoute ? 'Edit Quick Route' : 'Add Quick Route'}
            </DialogTitle>
            <DialogDescription>
              {editingRoute ? 'Update the route details' : 'Add a commonly used route for quick access'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Origin *</Label>
              <Input
                placeholder="e.g., Calgary, AB"
                value={quickRouteForm.origin}
                onChange={(e) => setQuickRouteForm({ ...quickRouteForm, origin: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Destination *</Label>
              <Input
                placeholder="e.g., Grande Prairie, AB"
                value={quickRouteForm.destination}
                onChange={(e) => setQuickRouteForm({ ...quickRouteForm, destination: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Display Name (optional)</Label>
              <Input
                placeholder="Auto-generated if empty"
                value={quickRouteForm.name}
                onChange={(e) => setQuickRouteForm({ ...quickRouteForm, name: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to auto-generate from origin and destination
              </p>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setQuickRouteDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={saveQuickRoute}
                disabled={savingRoute || !quickRouteForm.origin || !quickRouteForm.destination}
              >
                {savingRoute ? 'Saving...' : (editingRoute ? 'Update Route' : 'Add Route')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Override Dialog */}
      <Dialog open={overrideDialogOpen} onOpenChange={setOverrideDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Approve Cold Weather Override
            </DialogTitle>
            <DialogDescription>
              {selectedLocation && (
                <span>
                  Current temperature in <strong>{selectedLocation.location.name}</strong> is{' '}
                  <strong className="text-red-400">{Math.round(selectedLocation.weather.temp)}°C</strong>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
              <p className="text-sm text-red-400">
                <strong>Warning:</strong> This temperature is below the critical threshold. 
                By approving this override, you acknowledge the cold weather risks and take responsibility for this departure decision.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Reason for Override *</Label>
              <Textarea
                placeholder="e.g., Urgent delivery, driver has cold weather equipment, short distance..."
                value={overrideForm.reason}
                onChange={(e) => setOverrideForm({ ...overrideForm, reason: e.target.value })}
                rows={3}
                data-testid="override-reason"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Override Duration</Label>
              <Select 
                value={overrideForm.duration_hours} 
                onValueChange={(v) => setOverrideForm({ ...overrideForm, duration_hours: v })}
              >
                <SelectTrigger data-testid="override-duration">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 hours</SelectItem>
                  <SelectItem value="4">4 hours</SelectItem>
                  <SelectItem value="8">8 hours</SelectItem>
                  <SelectItem value="12">12 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setOverrideDialogOpen(false)}>Cancel</Button>
              <Button 
                variant="destructive" 
                onClick={handleOverride}
                disabled={submittingOverride || !overrideForm.reason}
                data-testid="confirm-override-btn"
              >
                {submittingOverride ? 'Approving...' : 'Approve Override'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Location Management Dialog */}
      <Dialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              {editingLocation ? 'Edit Location' : 'Add Weather Location'}
            </DialogTitle>
            <DialogDescription>
              {editingLocation 
                ? 'Update the location details below.' 
                : 'Add a new location to monitor weather. Just enter the name and we\'ll find the coordinates automatically, or enter lat/long manually.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="loc-name">Location Name *</Label>
              <Input
                id="loc-name"
                placeholder="e.g., Fort Nelson, Dawson Creek"
                value={locationForm.name}
                onChange={(e) => setLocationForm(prev => ({ ...prev, name: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">We'll automatically find coordinates for Canadian locations</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="loc-lat">Latitude (optional)</Label>
                <Input
                  id="loc-lat"
                  type="number"
                  step="any"
                  placeholder="e.g., 58.8050"
                  value={locationForm.lat}
                  onChange={(e) => setLocationForm(prev => ({ ...prev, lat: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="loc-lon">Longitude (optional)</Label>
                <Input
                  id="loc-lon"
                  type="number"
                  step="any"
                  placeholder="e.g., -122.7002"
                  value={locationForm.lon}
                  onChange={(e) => setLocationForm(prev => ({ ...prev, lon: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="loc-province">Province</Label>
              <Select 
                value={locationForm.province} 
                onValueChange={(value) => setLocationForm(prev => ({ ...prev, province: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select province" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BC">British Columbia</SelectItem>
                  <SelectItem value="AB">Alberta</SelectItem>
                  <SelectItem value="SK">Saskatchewan</SelectItem>
                  <SelectItem value="YT">Yukon</SelectItem>
                  <SelectItem value="NT">Northwest Territories</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-secondary/50 rounded-lg p-3 text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">Finding Coordinates</p>
              <p>You can find lat/long coordinates using Google Maps: right-click on a location and select "What's here?" to see the coordinates.</p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setLocationDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveLocation} disabled={savingLocation}>
              {savingLocation ? 'Saving...' : (editingLocation ? 'Update Location' : 'Add Location')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
